import { useEffect, useState, useRef, useCallback } from "react";

import { Box, CssBaseline, Fade } from "@mui/material";
import dynamic from "next/dynamic";

import { ZeffyConfetti } from "../../Animations/ZeffyConfetti/ZeffyConfetti";
import { useArrow } from "../AnimatePreview/Arrow/ArrowContext";
import { ArrowPreview } from "../AnimatePreview/Arrow/ArrowPreview";
import { useResponsivePhonePreview } from "../useResponsivePhonePreview";

import { getDotGridBackground } from "./helpers/dotGridBackground";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { MobileDesktopSwitch } from "@/components/LiveEditorElements/MobileDesktopSwitch";
import DesktopFrame from "@/icons/illustration/desktop-frame.svg";
import PhoneFrame from "@/icons/illustration/iphone-frame.svg";

const IframeRenderer = dynamic(() => import("../IframeRenderer").then((mod) => mod.IframeRenderer), {
  ssr: false,
});

export type DeviceSwitchablePreviewLayoutProps = {
  currentStepIndex: number;
  previewDevice: "mobile" | "desktop";
  setPreviewDevice: (device: "mobile" | "desktop") => void;
  setPreviewScale: (scale: number) => void;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /**
   * Whether to add top padding in mobile preview.
   */
  hasPreviewPadding?: boolean;
  /**
   * Whether to show confetti animation.
   */
  showConfetti?: boolean;
  slots: {
    content: React.ReactNode;
    footerHint?: React.ReactNode;
  };
};

export const DeviceSwitchablePreviewLayout = ({
  currentStepIndex,
  previewDevice,
  setPreviewDevice,
  setPreviewScale,
  iframeRef,
  hasPreviewPadding = false,
  showConfetti = false,
  slots,
}: DeviceSwitchablePreviewLayoutProps) => {
  const { isSmallScreen } = useMediaQuery();
  const { hideArrow } = useArrow();

  const desktopOriginalWidth = 1512;
  const desktopOriginalHeight = 912.06;
  const maxDesktopWidth = 665.27; // Max width for desktop preview
  const desktopAspectRatio = desktopOriginalWidth / desktopOriginalHeight;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);

  // This state controls the fade in/out of the content
  const [isVisible, setIsVisible] = useState(true);

  // Responsive phone preview dimensions
  const {
    phoneHeight,
    phoneWidth,
    phoneScale,
    originalWidth: phoneOriginalWidth,
    originalHeight: phoneOriginalHeight,
  } = useResponsivePhonePreview();

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;
        setContainerSize({ width, height });
      }
    };

    updateContainerSize();

    // Add event listener for window resize
    window.addEventListener("resize", updateContainerSize);
    return () => {
      window.removeEventListener("resize", updateContainerSize);
    };
  }, [previewDevice]);
  const desktopWidthPadding = 14;
  const desktopWidth = Math.min(containerSize?.width || maxDesktopWidth, maxDesktopWidth);
  const currentHeight = previewDevice === "mobile" ? phoneHeight : desktopWidth / desktopAspectRatio;
  const currentScale = previewDevice === "mobile" ? phoneScale : desktopWidth / desktopOriginalWidth;

  // Update preview scale in context whenever it changes
  useEffect(() => {
    setPreviewScale(currentScale);
  }, [currentScale, setPreviewScale]);

  const handleSwitchView = useCallback(
    (view: "desktop" | "mobile") => {
      hideArrow();
      // First, fade out
      setIsVisible(false);

      // After fade-out transition ends, switch the view and then fade in again
      setTimeout(() => {
        setPreviewDevice(view); // Toggle the viewport mode
        // Wait an additional 100ms for iframe content to settle before fading in
        setTimeout(() => setIsVisible(true), 100);
      }, 300); // 300ms matches the Fade transition duration
    },
    [setPreviewDevice, hideArrow, setIsVisible]
  );

  const isPhonePreview = previewDevice === "mobile";

  useEffect(() => {
    if (isSmallScreen && previewDevice !== "mobile") {
      setPreviewDevice("mobile");
    }
  }, [isSmallScreen, previewDevice, setPreviewDevice]);

  const renderIframe = ({ fullBleed }: { fullBleed?: boolean } = {}) => (
    <IframeRenderer
      iframeRef={iframeRef}
      width={
        fullBleed
          ? "100%"
          : isPhonePreview
            ? `${phoneOriginalWidth}px`
            : `${desktopOriginalWidth + desktopWidthPadding}px`
      }
      height={fullBleed ? "100%" : isPhonePreview ? `${phoneOriginalHeight}px` : `${desktopOriginalHeight}px`}
      scale={fullBleed ? 1 : currentScale}
      disableBorderRadius={Boolean(fullBleed) || !isPhonePreview}
      style={{
        boxSizing: "border-box",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 1,
        ...(fullBleed
          ? { padding: 0, right: 0, bottom: 0 }
          : isPhonePreview
            ? {
                // these are the dimensions of the border of the phone frame
                paddingRight: "14px",
                paddingLeft: "14px",
                paddingTop: "13px",
                paddingBottom: "13px",
              }
            : {
                paddingTop: "71.95px",
                paddingBottom: "31.22px",
                paddingLeft: "26.35px",
                paddingRight: "26.35px",
              }),
      }}
    >
      <CssBaseline />
      <Box
        sx={{
          pointerEvents: "none",
          "& *": {
            pointerEvents: "none !important",
          },
          width: "100%",
          height: "100%",
          paddingTop: isPhonePreview && hasPreviewPadding ? 2 : 0,
        }}
        data-test="preview-slots-wrapper"
      >
        {slots.content}
      </Box>
    </IframeRenderer>
  );

  if (isSmallScreen) {
    return (
      <Box
        data-test="live-preview"
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        {showConfetti && <ZeffyConfetti canLoadData={true} />}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
          }}
        >
          {renderIframe({ fullBleed: true })}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={(theme) => ({
        minHeight: "100%",
        ...getDotGridBackground(theme),
        paddingX: isPhonePreview ? 0 : 2, // Add horizontal padding for desktop view
        position: "relative",
      })}
      data-test="live-preview"
    >
      {showConfetti && <ZeffyConfetti canLoadData={true} />}

      {/* Toggle Switch */}
      <MobileDesktopSwitch paddingTop={3} value={previewDevice} onChange={handleSwitchView} />

      {/* Preview container */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingTop={3}
        paddingBottom={{ xs: 7, md: 2, lg: 2 }}
        width="100%"
        height="100%"
        ref={containerRef}
        data-test="preview-container"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="relative"
          overflow={isPhonePreview ? "inherit" : "hidden"}
          borderRadius={isPhonePreview ? 0 : "17px"}
          width="100%"
          maxWidth={isPhonePreview ? phoneWidth : maxDesktopWidth}
          height={currentHeight}
          data-test="preview-wrapper"
        >
          <Fade in={isVisible} timeout={300}>
            <Box
              position="relative"
              width={isPhonePreview ? phoneWidth : "100%"}
              height={isPhonePreview ? phoneHeight : "100%"}
              data-test="preview-wrapper-inner"
            >
              {isPhonePreview && <ArrowPreview iframeRef={iframeRef} currentStepIndex={currentStepIndex} />}

              {/* Frames */}
              <Box
                position="absolute"
                width="100%"
                height="100%"
                zIndex={2}
                data-test="frame"
                sx={{ pointerEvents: "none" }}
              >
                {isPhonePreview ? <PhoneFrame /> : <DesktopFrame />}
              </Box>

              {renderIframe()}
            </Box>
          </Fade>
        </Box>

        {slots.footerHint}
      </Box>
    </Box>
  );
};
